import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Col, Container, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment, { Moment } from "moment";
import {
  HISTORY_POST_LOADING,
  OPENAI_GET_TEMPLATES_LOADING,
} from "../../constants/loading.constant";
import { useGlobalCore } from "../../cores/globals";
import { useSocialPostCore } from "../../cores/socialPost";
import { useTwitterCore } from "../../cores/twitter";
import { useFacebookCore } from "../../cores/facebook";
import { useInstagramCore } from "../../cores/instagram";
import { useLinkedInCore } from "../../cores/linkedIn";
import { useLinkedInBusinessCore } from "../../cores/linkedInBusiness";
import { useTiktokCore } from "../../cores/tiktok";
import { Calendar } from "../component/socialCalendar/Calendar";
import { SocialCalendarBar } from "../component/socialCalendar/SocialCalendarBar";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import SideModal from "../layout/SideModal";
import { useIntl } from "react-intl";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { MONTH } from "../utils/dateUtils";
import {
  ADD_MENTION_MODAL,
  ADD_SOCIAL_IMAGE_MODAL,
  ADD_SOCIAL_VIDEO_MODAL,
  EMOJI_FORM,
  HASHTAGS_SOCIAL_FORM,
  OPENAI_SOCIAL_FORM,
  SCHEDULE_POST_MODAL,
  TEMPLATES_SOCIAL_FORM,
} from "../../constants/modal.constant";
import { SocialCalendarPostComposer } from "../component/socialCalendar/SocialCalendarPostComposer";
import { PostForm } from "../component/socialPost/PostForm";
import ImageForm from "../component/socialPost/modals/ImageForm";
import VideoForm from "../component/socialPost/modals/VideoForm";
import { MentionForm } from "../component/socialPost/modals/MentionForm";
import HashtagsForm from "../component/socialPost/modals/HashtagsForm";
import TemplatesForm from "../component/socialPost/modals/TemplatesForm";
import OpenAiForm from "../component/socialPost/modals/OpenAiForm";
import { SocialType } from "../../cores/socialPost/interfaces/postError";
import {
  Media,
  SocialPost,
} from "../../cores/socialPost/interfaces/api/get-social-posts";
import { PostImage } from "../../cores/socialPost/interfaces/postImage";
import { PostVideo } from "../../cores/socialPost/interfaces/postVideo";
import { SOCIAL_POST_TYPES } from "../../cores/socialPost/consts/socialPostType.constant";
import { updateSocialPostSuccessAction } from "../../cores/socialPost/store/api-actions";
import { scheduleDateStringForApiFromDateAndHour } from "../../cores/socialPost/helpers/scheduleDateForApi";
import {
  loadPostImagesFromMediaUrls,
  loadPostVideoFromMediaUrl,
} from "../../cores/socialPost/helpers/loadPostMediaFromUrls";

export default function SocialCalendar() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const {
    socialPosts,
    date,
    setDate,
    reset,
    resetComposerDraft,
    selectPost,
    setDuplicateScheduleSourcePostId,
    removeAllImages,
    removeVideo,
    getSocialPosts,
  } = useSocialPostCore();
  const {
    isModal,
    turnOffModal,
    getModalContent,
    getModalVisibility,
    isLoading,
  } = useGlobalCore();
  const { companyRole, isAdmin, isReseller, getMeSocialSettings, company } =
    useMeCore();
  const { modifySocialPost: modifyTwitter, modifySocialPostVideo: modifyTwitterVideo } = useTwitterCore();
  const { modifySocialPost: modifyFacebook, modifySocialPostVideo: modifyFacebookVideo } = useFacebookCore();
  const { modifySocialPost: modifyInstagram, modifySocialPostVideo: modifyInstagramVideo } = useInstagramCore();
  const { modifySocialPost: modifyLinkedIn } = useLinkedInCore();
  const { modifySocialPost: modifyLinkedInBusiness, modifySocialPostVideo: modifyLinkedInBusinessVideo } = useLinkedInBusinessCore();
  const { modifySocialPost: modifyTiktok, modifySocialPostVideo: modifyTiktokVideo } = useTiktokCore();
  const { goToSocial } = useRouterCore();
  const [calendarView, setCalendarView] = useState<"day" | "week" | "month">(
    "month"
  );
  const [bestHoursNetwork, setBestHoursNetwork] = useState<
    "none" | "facebook" | "instagram" | "linkedin"
  >("none");
  const [showBestHoursPercent, setShowBestHoursPercent] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerDate, setComposerDate] = useState<Moment | undefined>(undefined);
  const [calendarFilterProvider, setCalendarFilterProvider] = useState("all");
  const [calendarFilterTarget, setCalendarFilterTarget] = useState("post");

  const refreshCalendarPosts = useCallback(() => {
    if (!company?.id) return;
    const rangeStart =
      calendarView === "day"
        ? moment(date).startOf("day")
        : calendarView === "week"
        ? moment(date).startOf("week")
        : moment(date).startOf("month");
    const rangeEnd =
      calendarView === "day"
        ? moment(date).endOf("day")
        : calendarView === "week"
        ? moment(date).endOf("week")
        : moment(date).endOf("month");
    getSocialPosts(
      company.id,
      rangeStart.format("YYYY-MM-DD"),
      rangeEnd.format("YYYY-MM-DD"),
      calendarFilterProvider,
      "",
      calendarFilterTarget
    );
  }, [
    company?.id,
    calendarView,
    date,
    calendarFilterProvider,
    calendarFilterTarget,
    getSocialPosts,
  ]);

  const browserTimeZone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  }, []);

  /* Mismo criterio que SocialPost: el selector de redes y el envío usan datos de getMeSocialSettings. */
  useEffect(() => {
    if (company?.id) getMeSocialSettings(company.id);
  }, [company?.id, getMeSocialSettings]);

  useEffect(() => {
    if (
      !companyRole?.social &&
      !companyRole?.owner &&
      !companyRole?.admin &&
      !companyRole?.agent &&
      !isAdmin &&
      !isReseller
    ) goToSocial();
  }, [goToSocial, companyRole, isAdmin, isReseller]);

  useEffect(() => {}, [socialPosts]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(new Date());
    }, 30000);
    return () => window.clearInterval(timer);
  }, []);

  const currentTime = useMemo(
    () =>
      now.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
    [now]
  );

  const nextPeriod = useCallback(() => {
    if (calendarView === "day") {
      setDate(moment(date).add(1, "d"));
      return;
    }
    if (calendarView === "week") {
      setDate(moment(date).add(1, "w"));
      return;
    }
    setDate(moment(date).add(1, "M"));
  }, [calendarView, date, setDate]);

  const previousPeriod = useCallback(() => {
    if (calendarView === "day") {
      setDate(moment(date).subtract(1, "d"));
      return;
    }
    if (calendarView === "week") {
      setDate(moment(date).subtract(1, "w"));
      return;
    }
    setDate(moment(date).subtract(1, "M"));
  }, [calendarView, date, setDate]);

  const openCreatePostModal = useCallback(
    (day: Moment, hour?: number) => {
      const selected = hour !== undefined ? moment(day).hour(hour).minute(0) : moment(day);
      /* No usar reset(): vacía socialPosts en el store y los posteos del calendario “desaparecen”. */
      resetComposerDraft();
      setComposerDate(selected);
      setComposerOpen(true);
    },
    [resetComposerDraft]
  );

  const openEditPostModal = useCallback(
    (post: SocialPost) => {
      setDuplicateScheduleSourcePostId(null);
      removeVideo();
      removeAllImages();
      const m = moment(post.post.postAt || post.post.scheduleDate);
      if (!post.post.postAt && m.isValid()) {
        selectPost({
          ...post,
          post: {
            ...post.post,
            scheduleDate: scheduleDateStringForApiFromDateAndHour(
              m.toDate(),
              m.format("HH:mm")
            ),
          },
        });
      } else {
        selectPost(post);
      }
      setComposerDate(m);
      setComposerOpen(true);
    },
    [
      removeAllImages,
      removeVideo,
      selectPost,
      setDuplicateScheduleSourcePostId,
    ]
  );

  const onReschedulePost = useCallback(
    async (post: SocialPost, nextMoment: Moment) => {
      if (!company?.id) return;
      if (post.post.postAt) return; // no mover publicados
      const oldMoment = moment(post.post.scheduleDate);
      if (!nextMoment.isValid()) return;
      const nextStr = scheduleDateStringForApiFromDateAndHour(
        nextMoment.toDate(),
        nextMoment.format("HH:mm")
      );
      const ok = window.confirm(
        `¿Reagendar publicación?\n\nDe: ${oldMoment.isValid() ? oldMoment.format("DD/MM/YYYY HH:mm") : post.post.scheduleDate}\nA: ${nextMoment.format("DD/MM/YYYY HH:mm")}`
      );
      if (!ok) return;

      const provider = (post.post.socialProvider || "").toLowerCase();
      const mentions: any = post.mention || [];
      const media: Media[] = post.media || [];
      const txt = post.post.text || "";
      const hasVideo =
        post.post.type === SOCIAL_POST_TYPES.VIDEO && media.length > 0;

      const onSuccess = () => {
        dispatch(
          updateSocialPostSuccessAction({
            post: {
              ...post,
              post: { ...post.post, scheduleDate: nextStr },
            },
          })
        );
      };

      let images: PostImage[] = [];
      let video: PostVideo | null = null;
      try {
        if (hasVideo) {
          video = await loadPostVideoFromMediaUrl(media);
          if (!video) {
            window.alert(
              "No se pudo cargar el video del post. Ábrelo desde el calendario y guarda de nuevo."
            );
            return;
          }
        } else if (media.length > 0) {
          images = await loadPostImagesFromMediaUrls(media);
          if (images.length === 0) {
            window.alert(
              "No se pudieron cargar las imágenes del post. Ábrelo desde el calendario y guarda de nuevo."
            );
            return;
          }
        }
      } catch (e) {
        console.error(e);
        window.alert(
          "No se pudieron cargar los archivos del post para reagendar. Inténtalo de nuevo o edita el post."
        );
        return;
      }

      switch (provider) {
        case "twitter":
          hasVideo
            ? modifyTwitterVideo(
                company.id,
                post.post.id,
                txt,
                mentions,
                video,
                nextStr,
                media,
                onSuccess
              )
            : modifyTwitter(
                company.id,
                post.post.id,
                txt,
                mentions,
                images,
                nextStr,
                media,
                onSuccess
              );
          break;
        case "facebook":
          hasVideo
            ? modifyFacebookVideo(
                company.id,
                post.post.id,
                txt,
                mentions,
                video,
                nextStr,
                media,
                onSuccess
              )
            : modifyFacebook(
                company.id,
                post.post.id,
                txt,
                mentions,
                images,
                nextStr,
                media,
                onSuccess
              );
          break;
        case "instagram":
          hasVideo
            ? modifyInstagramVideo(
                company.id,
                post.post.id,
                txt,
                mentions,
                video,
                nextStr,
                media,
                onSuccess
              )
            : modifyInstagram(
                company.id,
                post.post.id,
                txt,
                mentions,
                images,
                nextStr,
                media,
                onSuccess
              );
          break;
        case "linkedin":
          modifyLinkedIn(
            company.id,
            post.post.id,
            txt,
            mentions,
            images,
            nextStr,
            media,
            onSuccess
          );
          break;
        case "linkedinbusiness":
          hasVideo
            ? modifyLinkedInBusinessVideo(
                company.id,
                post.post.id,
                txt,
                mentions,
                video,
                nextStr,
                media,
                onSuccess
              )
            : modifyLinkedInBusiness(
                company.id,
                post.post.id,
                txt,
                mentions,
                images,
                nextStr,
                media,
                onSuccess
              );
          break;
        case "tiktok":
          hasVideo
            ? modifyTiktokVideo(
                company.id,
                post.post.id,
                txt,
                mentions,
                video,
                nextStr,
                media,
                onSuccess
              )
            : modifyTiktok(
                company.id,
                post.post.id,
                txt,
                mentions,
                images,
                nextStr,
                media,
                onSuccess
              );
          break;
        default:
          openEditPostModal(post);
          break;
      }
    },
    [
      company?.id,
      dispatch,
      modifyTwitter,
      modifyTwitterVideo,
      modifyFacebook,
      modifyFacebookVideo,
      modifyInstagram,
      modifyInstagramVideo,
      modifyLinkedIn,
      modifyLinkedInBusiness,
      modifyLinkedInBusinessVideo,
      modifyTiktok,
      modifyTiktokVideo,
      openEditPostModal,
    ]
  );
 
  return (
    <Layout>
      <Header2 
        title={intl.formatMessage({ id: "app.sidebar.socialMediaCalendar" })}
      />
      <Content>
        <Container fluid className="social-calendar-page">
          <Row>
            <Col xs="12">
              <SocialCalendarBar
                calendarView={calendarView}
                onChangeCalendarView={setCalendarView}
                bestHoursNetwork={bestHoursNetwork}
                onChangeBestHoursNetwork={setBestHoursNetwork}
                showBestHoursPercent={showBestHoursPercent}
                onToggleBestHoursPercent={setShowBestHoursPercent}
                filterProvider={calendarFilterProvider}
                filterTarget={calendarFilterTarget}
                onFilterProviderChange={setCalendarFilterProvider}
                onFilterTargetChange={setCalendarFilterTarget}
              />
              <UpdateLoadingCard
                loadingId={HISTORY_POST_LOADING}
                text="Actualizando Publicaciones"
              />
              <div className="social-calendar-top-meta">
                <div className="social-calendar-top-meta__right">
                  <div className="social-calendar-bar__period-nav social-calendar-top-meta__period mr-2">
                    <button
                      type="button"
                      className="social-calendar-bar__nav-btn"
                      onClick={previousPeriod}
                      aria-label="Anterior"
                    >
                      <FontAwesomeIcon
                        icon={["fas", "chevron-left"]}
                        size="1x"
                        color="#64748B"
                      />
                    </button>
                    <span className="social-calendar-bar__month-label">
                      {calendarView === "day"
                        ? moment(date).format("DD MMM YYYY")
                        : calendarView === "week"
                        ? `${moment(date).startOf("week").format("DD MMM")} - ${moment(date)
                            .endOf("week")
                            .format("DD MMM YYYY")}`
                        : MONTH[moment(date).format("M")] + " " + moment(date).format("YYYY")}
                    </span>
                    <button
                      type="button"
                      className="social-calendar-bar__nav-btn"
                      onClick={nextPeriod}
                      aria-label="Siguiente"
                    >
                      <FontAwesomeIcon
                        icon={["fas", "chevron-right"]}
                        size="1x"
                        color="#64748B"
                      />
                    </button>
                  </div>
                  <button
                    type="button"
                    className="social-calendar-top-meta__today-btn mr-2"
                    onClick={() => setDate(moment())}
                  >
                    {intl.formatMessage({ id: "app.social.goToday" })}
                  </button>
                  <div className="social-calendar-timezone-pill">
                    <FontAwesomeIcon icon={["far", "clock"]} className="mr-2" />
                    <span>{`${currentTime} - ${browserTimeZone}`}</span>
                    <FontAwesomeIcon
                      icon={["fas", "chevron-down"]}
                      className="ml-2"
                      size="sm"
                    />
                  </div>
                </div>
              </div>
              <Container
                fluid
                className="d-flex flex-wrap justify-content-center align-items-baseline p-0"
              >
                <Calendar
                  view={calendarView}
                  bestHoursNetwork={bestHoursNetwork}
                  showBestHoursPercent={showBestHoursPercent}
                  onCreatePostAt={openCreatePostModal}
                  onOpenPostComposer={openEditPostModal}
                  onReschedulePost={onReschedulePost}
                />
              </Container>
            </Col>
          </Row>
        </Container>
      </Content>
      <Modal
        isOpen={composerOpen}
        toggle={() => {
          setComposerOpen(false);
          setDuplicateScheduleSourcePostId(null);
        }}
        centered
        size="xl"
        className="social-calendar-composer-modal"
      >
        <ModalHeader
          toggle={() => {
            setComposerOpen(false);
            setDuplicateScheduleSourcePostId(null);
          }}
        >
          {intl.formatMessage({ id: "app.socialPostSelector.createTitle" })}
        </ModalHeader>
        <ModalBody>
          <SocialCalendarPostComposer
            selectedDate={composerDate}
            onChangeSelectedDate={setComposerDate}
            onScheduledSuccess={() => setComposerOpen(false)}
            onSavedSuccess={() => setComposerOpen(false)}
            onDeletedSuccess={() => setComposerOpen(false)}
            onPostsRefresh={refreshCalendarPosts}
          />
        </ModalBody>
      </Modal>
      <SideModal
        active={isModal(SCHEDULE_POST_MODAL)}
        visible={getModalVisibility(SCHEDULE_POST_MODAL)}
        onClose={() => turnOffModal(SCHEDULE_POST_MODAL)}
      >
        {getModalContent(SCHEDULE_POST_MODAL) || (
          <PostForm
            onClose={() => turnOffModal(SCHEDULE_POST_MODAL)}
            onReset={reset}
            repost={false}
            socialType={SocialType.Post}
          />
        )}
      </SideModal>
      <SideModal
        active={isModal(ADD_SOCIAL_IMAGE_MODAL)}
        visible={getModalVisibility(ADD_SOCIAL_IMAGE_MODAL)}
        onClose={() => turnOffModal(ADD_SOCIAL_IMAGE_MODAL)}
      >
        <ImageForm />
      </SideModal>
      <SideModal
        active={isModal(ADD_SOCIAL_VIDEO_MODAL)}
        visible={getModalVisibility(ADD_SOCIAL_VIDEO_MODAL)}
        onClose={() => turnOffModal(ADD_SOCIAL_VIDEO_MODAL)}
      >
        <VideoForm />
      </SideModal>
      <SideModal
        active={isModal(EMOJI_FORM)}
        visible={getModalVisibility(EMOJI_FORM)}
        onClose={() => turnOffModal(EMOJI_FORM)}
      >
        {getModalContent(EMOJI_FORM)}
      </SideModal>
      <SideModal
        active={isModal(TEMPLATES_SOCIAL_FORM)}
        visible={getModalVisibility(TEMPLATES_SOCIAL_FORM)}
        onClose={() => turnOffModal(TEMPLATES_SOCIAL_FORM)}
      >
        <TemplatesForm />
      </SideModal>
      <SideModal
        active={isModal(OPENAI_SOCIAL_FORM)}
        visible={getModalVisibility(OPENAI_SOCIAL_FORM)}
        onClose={() => turnOffModal(OPENAI_SOCIAL_FORM)}
        showSppiner={isLoading(OPENAI_GET_TEMPLATES_LOADING)}
      >
        <OpenAiForm />
      </SideModal>
      <SideModal
        active={isModal(ADD_MENTION_MODAL)}
        visible={getModalVisibility(ADD_MENTION_MODAL)}
        onClose={() => turnOffModal(ADD_MENTION_MODAL)}
      >
        <MentionForm />
      </SideModal>
      <SideModal
        active={isModal(HASHTAGS_SOCIAL_FORM)}
        visible={getModalVisibility(HASHTAGS_SOCIAL_FORM)}
        onClose={() => turnOffModal(HASHTAGS_SOCIAL_FORM)}
      >
        <HashtagsForm />
      </SideModal>
    </Layout>
  );
}
